import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CircularProgress } from '@mui/material';
import moment from 'moment';
import Header from '../header';
import InputField from '../inputField';
import InfoRow from '../infoRow';
import {
  AjudArchiveData,
  fetchAjudArchiveData,
  ModalBlockProps,
  moneyMask,
  updateAjudUnlock,
} from './service';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxHeight: '90vh', // Limita a altura máxima do modal
  overflowY: 'auto', // Adiciona rolagem vertical quando necessário
  bgcolor: 'background.paper',
  border: '2px solid #cecece',
  borderRadius: 5,
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

const ModalBlock = ({ open, ajudValue, closeModal }: ModalBlockProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inputDisable, setInputDisable] = useState(false);
  const [data, setData] = useState<AjudArchiveData>(); // Estado para armazenar a resposta
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const resetState = () => {
    setPage(0);
    setRowsPerPage(5);
  };

  const handleClose = (shouldReload?: boolean) => {
    resetState();
    closeModal(shouldReload);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const response = await fetchAjudArchiveData(ajudValue?.court_order || '');
      if (response) setData(response);
      setIsLoading(false);
    };

    fetchData();
  }, [ajudValue?.court_order]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, data?.data.length || 0 - page * rowsPerPage);

  const formik = useFormik({
    initialValues: {
      response_code: ajudValue?.response_code || '01',
      available_value_unlock: ajudValue?.available_value_unlock || '',
      lock_remaining: ajudValue?.lock_remaining || '',
    },
    validationSchema: Yup.object({
      response_code: Yup.string().required('Código resposta é obrigatório'),
      available_value_unlock: Yup.string().when('response_code', {
        is: (code: string) => code !== '07' && code !== '47', // Verifica se não é 07 ou 47
        then: (schema) => schema.required('Valor disponível é obrigatório'),
      }),

      lock_remaining: Yup.string().when('response_code', {
        is: (code: string) => code !== '07' && code !== '47', // Verifica se não é 07 ou 47
        then: (schema) => schema.required('Valor restante é obrigatório'),
      }),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true);

      const sanitizedValues = {
        ...values,
        available_value_unlock: moneyMask(values.available_value_unlock),
        lock_remaining: moneyMask(values.lock_remaining),
      };

      const { success, message } = await updateAjudUnlock(
        ajudValue?.id || '',
        sanitizedValues,
      );

      if (success) {
        toast.success('Desbloqueio atualizado com sucesso');
        handleClose(true);
      } else {
        toast.error(`Erro ao atualizar o desbloqueio: ${message}`);
      }

      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (
      formik.values.response_code === '07' ||
      formik.values.response_code === '47'
    ) {
      setInputDisable(true);
      formik.setFieldValue('lock_remaining', '0');
      formik.setFieldValue('available_value_unlock', '0');
    } else {
      setInputDisable(false);
    }
  }, [formik.values.response_code, formik.setFieldValue]);

  return (
    <Modal open={open} onClose={() => handleClose(true)}>
      <Box sx={style} component="form" onSubmit={formik.handleSubmit}>
        <Header
          title={`Detalhes do AJUD: ${ajudValue?.response_code == null ? '' : ajudValue?.response_code}`}
          subtitle={ajudValue?.court_order}
          label="DESBLOQUEIO"
          labelColor="red"
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              marginTop: 1,
            }}
          >
            <InfoRow
              label="Ordem Judicial:"
              value={ajudValue?.court_order || ''}
            />
            <InfoRow
              label="  Valor de desbloqueio:"
              value={ajudValue?.value_unlock || ''}
            />
          </Box>
          {data?.data && data.data.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Data de Envio
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Ordem
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Documento
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Valor Bloqueado
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Status Ordem
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(
                    data?.data.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    ) || []
                  ).map((item, key) => (
                    <TableRow key={key}>
                      <TableCell>
                        {moment(item.shipping_date, 'DDMMYYYY').format(
                          'DD/MM/yyyy',
                        )}
                      </TableCell>
                      <TableCell>{item.court_order}</TableCell>
                      <TableCell>{item.cnpj_cpf_oj}</TableCell>
                      <TableCell>
                        {moneyMask(item.account_value_available)}
                      </TableCell>
                      <TableCell>
                        {item.canceled_order === 'T' ? (
                          <Typography color="red" fontSize={14}>
                            Ordem cancelada
                          </Typography>
                        ) : item.canceled_order === 'F' ? (
                          'Ordem reiterada'
                        ) : (
                          ''
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5]}
                component="div"
                count={data?.data.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
              />
            </TableContainer>
          )}
        </Box>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Atualize as informações de desbloqueio
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <FormControl
            fullWidth
            error={Boolean(
              formik.errors.response_code && formik.touched.response_code,
            )}
          >
            <InputLabel id="dropdown-label">Código resposta</InputLabel>
            <Select
              label="Código resposta"
              labelId="dropdown-label"
              name="response_code"
              disabled={isLoading}
              value={formik.values.response_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <MenuItem value="01">01 - Cumprida integralmente</MenuItem>
              <MenuItem value="07">
                07 - Operação já realizada fora do sistema Sisbajud
              </MenuItem>
            </Select>
            {formik.touched.response_code && formik.errors.response_code && (
              <FormHelperText>{formik.errors.response_code}</FormHelperText>
            )}
          </FormControl>
          <InputField
            id="available_value_unlock"
            name="available_value_unlock"
            label="Valor disponível"
            value={moneyMask(formik.values.available_value_unlock)}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.available_value_unlock}
            touched={formik.touched.available_value_unlock}
            disabled={isLoading || inputDisable}
          />
          <InputField
            id="lock_remaining"
            name="lock_remaining"
            label="Valor restante"
            value={moneyMask(formik.values.lock_remaining)}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.lock_remaining}
            touched={formik.touched.lock_remaining}
            disabled={isLoading || inputDisable}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 2,
            bottom: 16,
            right: 16,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading} // Desabilitar quando estiver carregando
            startIcon={
              isLoading && <CircularProgress size={20} color="inherit" />
            } // Ícone de carregamento
          >
            {isLoading ? 'Atualizando...' : 'Atualizar informações'}
          </Button>
          <Button
            sx={{ ml: 2 }}
            disabled={isLoading}
            variant="contained"
            color="error"
            onClick={() => handleClose()}
          >
            Cancelar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalBlock;
